// eslint-disable-next-line simple-import-sort/imports
import { CHAIN_ID } from '@derivation-tech/context';
import { useEffect, useMemo } from 'react';

import { FETCHING_STATUS } from '@/constants';
import { WrappedPortfolio } from '@/entities/WrappedPortfolio';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useDappChainConfig, useUserAddr } from '@/hooks/web3/useChain';
import { TokenInfo } from '@/types/token';

import { QUERY_KEYS } from '@/constants/query';
import { PortfolioSocketParam, SOCKET_EVENTS, SOCKET_METHODS, SOCKET_TYPES } from '@/entities/SocketService';
import { queryClient } from '@/global';
import useSocket from '@/hooks/useSocket';

import { IGateBalanceInfo } from '@/types/balance';
import { ISocketPortfolioEvent } from '@/types/socket';
import { reduceAccounts } from '@/utils/account';
import { currentUnixTime } from '@/utils/timeUtils';
import { isNativeTokenAddr } from '@/utils/token';
import { getAccountId, getPortfolioId } from '@/utils/transform/transformId';
import { updatePortfolio, updatePortfolioList } from '../account/actions';
import { useFetchUserGateValue } from '../balance/query';
import { parseInstrumentToMeta } from '../futures/actions';
import { useFetchPortfolioListFromApi, useFetchSinglePortfolioFromApi } from './query';
import { selectOperateToken, selectSettleFetchingStatus, selectSettlePortfolioState } from './slice';

export function useSettlingPortfolio(
  chainId: CHAIN_ID | undefined,
  userAddr: string | undefined,
): WrappedPortfolio | undefined {
  const settlePortfolio = useAppSelector(selectSettlePortfolioState(chainId, userAddr));
  return settlePortfolio;
}

export function useSettlingStatus(chainId: CHAIN_ID | undefined, userAddr: string | undefined): FETCHING_STATUS {
  return useAppSelector(selectSettleFetchingStatus(chainId, userAddr));
}

export function usePortfolioOperateToken(
  chainId: CHAIN_ID | undefined,
  userAddr: string | undefined,
): TokenInfo | undefined {
  const operateToken = useAppSelector(selectOperateToken(chainId, userAddr));
  const chainConfig = useDappChainConfig(chainId);
  return useMemo(() => {
    if (operateToken) return operateToken;
    if (chainConfig) return chainConfig.wrappedNativeToken;
  }, [chainConfig, operateToken]);
}

export function useUserWithdrawPendingToken(
  chainId: CHAIN_ID | undefined,
  userAddr: string | undefined,
  tokenAddr: string | undefined,
): {
  pendingToken?: IGateBalanceInfo;
  isShowClaim: boolean;
  isShowProgress: boolean;
} {
  const dappConfig = useDappChainConfig(chainId);
  tokenAddr = tokenAddr && isNativeTokenAddr(tokenAddr) ? dappConfig?.wrappedNativeToken?.address : tokenAddr;

  const { data: gateValueResponse } = useFetchUserGateValue(chainId, userAddr);

  const pendingToken = useMemo(() => {
    if (gateValueResponse?.portfolios) {
      return gateValueResponse.portfolios.find((token) => token.address === tokenAddr);
    }
  }, [gateValueResponse?.portfolios, tokenAddr]);
  const isShowClaim = useMemo(() => {
    if (
      pendingToken?.pendingBalance?.amount?.gt(0) &&
      pendingToken?.pendingBalance?.timestamp &&
      gateValueResponse?.pendingDuration
    ) {
      const currentTime = currentUnixTime();
      if (currentTime > pendingToken?.pendingBalance?.timestamp + gateValueResponse?.pendingDuration) {
        return true;
      }
    }
    return false;
  }, [
    pendingToken?.pendingBalance?.amount,
    pendingToken?.pendingBalance?.timestamp,
    gateValueResponse?.pendingDuration,
  ]);

  const isShowProgress = useMemo(() => {
    return pendingToken?.pendingBalance?.amount?.gt(0) || false;
  }, [pendingToken?.pendingBalance?.amount]);

  return {
    isShowClaim,
    isShowProgress,
    pendingToken,
  };
}

export function useWithdrawPendingDurationHourly(chainId: CHAIN_ID | undefined): number | undefined {
  const userAddr = useUserAddr();
  const { data: gateValueResponse } = useFetchUserGateValue(chainId, userAddr);

  return useMemo(() => {
    if (gateValueResponse?.pendingDuration) return gateValueResponse?.pendingDuration / 3600;
  }, [gateValueResponse?.pendingDuration]);
}

export function useFetchPortfolioList(chainId: number | undefined, userAddr: string | undefined) {
  const { data: portfolioWithInstrumentList } = useFetchPortfolioListFromApi(chainId, userAddr);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (portfolioWithInstrumentList && chainId && userAddr) {
      if (portfolioWithInstrumentList?.instruments?.length) {
        dispatch(parseInstrumentToMeta({ chainId, instrumentList: portfolioWithInstrumentList?.instruments }));
      }
      const portfolios = portfolioWithInstrumentList?.portfolios;
      if (portfolios?.length) {
        const accountRecord = reduceAccounts(chainId, userAddr, portfolios);
        if (portfolios.length === 1) {
          const portfolio = portfolios[0];

          dispatch(
            updatePortfolio({
              chainId,
              userAddr,
              reducedPortfolio: accountRecord,
              instrumentAddr: portfolio.instrumentAddr,
              expiry: portfolio.expiry,
              block: portfolio?.blockInfo?.height || 0,
            }),
          );
        } else dispatch(updatePortfolioList({ chainId, userAddr, accountRecords: accountRecord }));
      }
    }
  }, [chainId, dispatch, portfolioWithInstrumentList, userAddr]);
}

export function useSinglePortfolio(
  chainId: number | undefined,
  userAddr: string | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) {
  const { data: portfolioWithInstrumentList } = useFetchSinglePortfolioFromApi(
    chainId,
    userAddr,
    instrumentAddr,
    expiry,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (portfolioWithInstrumentList && chainId && userAddr) {
      if (portfolioWithInstrumentList?.instruments?.length) {
        dispatch(parseInstrumentToMeta({ chainId, instrumentList: portfolioWithInstrumentList?.instruments }));
      }
      const portfolios = portfolioWithInstrumentList?.portfolios;
      if (portfolios?.length) {
        const accountRecord = reduceAccounts(chainId, userAddr, portfolios);
        if (portfolios.length === 1) {
          const portfolio = portfolios[0];

          dispatch(
            updatePortfolio({
              chainId,
              userAddr,
              reducedPortfolio: accountRecord,
              instrumentAddr: portfolio.instrumentAddr,
              expiry: portfolio.expiry,
              block: portfolio?.blockInfo?.height || 0,
            }),
          );
        } else dispatch(updatePortfolioList({ chainId, userAddr, accountRecords: accountRecord }));
      }
    }
  }, [chainId, dispatch, portfolioWithInstrumentList, userAddr]);

  const wrappedPortfolio = useMemo(() => {
    if (!chainId || !userAddr || !instrumentAddr || !expiry) return undefined;
    const portfolioId = getPortfolioId(getAccountId(userAddr, instrumentAddr), expiry);
    return WrappedPortfolio.getInstance(portfolioId, chainId);
  }, [chainId, expiry, instrumentAddr, userAddr]);

  return wrappedPortfolio;
}

export function useIsFetchedSinglePortfolio(
  chainId: number | undefined,
  userAddr: string | undefined,
  // instrumentAddr: string | undefined,
  // expiry: number | undefined,
) {
  // const { isFetched } = useFetchSinglePortfolioFromApi(chainId, userAddr);
  const { isFetched } = useFetchPortfolioListFromApi(chainId, userAddr);
  return isFetched;
}

export function useIsFetchedPortfolioList(chainId: number | undefined, userAddr: string | undefined) {
  // const { isFetched } = useFetchSinglePortfolioFromApi(chainId, userAddr);
  const { isFetched } = useFetchPortfolioListFromApi(chainId, userAddr);
  return isFetched;
}

export function useWatchPortfolioChange(chainId: number | undefined, userAddr: string | undefined) {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket || !chainId || !userAddr) return;
    const param = {
      chainId: chainId,
      userAddress: userAddr,
      type: SOCKET_TYPES.PORTFOLIO,
    } as PortfolioSocketParam;
    const subscribeId = Date.now();
    socket.emit({ id: subscribeId, method: SOCKET_METHODS.SUBSCRIBE, params: param });

    socket.on(SOCKET_EVENTS.PORTFOLIO_CHANGED, (data: ISocketPortfolioEvent) => {
      console.record('event', SOCKET_EVENTS.PORTFOLIO_CHANGED, data);
      if (data.type === 'gate') {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BALANCE.GATE(chainId, userAddr),
        });
      } else {
        if (data.instrument && data.expiry) {
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, data.instrument, data.expiry),
          });
        }
        if (data.userAddress) {
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr),
          });
        }
      }
    });

    return () => {
      socket.off(SOCKET_EVENTS.PORTFOLIO_CHANGED);
      socket.emit({ id: subscribeId, method: SOCKET_METHODS.UNSUBSCRIBE, params: param });
    };
  }, [chainId, socket, userAddr]);
}
